import { css, Global, useTheme } from '@emotion/react';
import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';

import { Row } from '../grid/Row';
import { Col } from '../grid/Col';
import { Title } from '../titles/Title';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  accessibilityTitle?: string,
  bottomSide?: ReactNode,
  className?: string,
  dataTest?: string,
  fullWidth?: Boolean,
  ignoreThemeHeight?: boolean,
  isFixed?: boolean,
  leftSide?: ReactNode,
  mode?: string,
  native?: { mt: number },
  /** Reverse the order in the DOM (but not visually), so that users will tab through the elements in reverse. */
  reverseOrder: boolean,
  rightSide?: ReactNode,
  sections: number,
  showLeftSideMobile?: boolean,
  topSide?: React.Node | string,
};

export const Footer = ({
  accessibilityTitle,
  bottomSide,
  className,
  dataTest,
  fullWidth,
  ignoreThemeHeight,
  isFixed,
  leftSide,
  mode,
  reverseOrder = false,
  rightSide,
  sections,
  showLeftSideMobile,
  topSide,
}: Props) => {
  const rightSideSize = 12 / sections;
  const leftSideSize = 12 - rightSideSize;
  const rightSideSizeMobile = showLeftSideMobile ? rightSideSize : 12;
  const leftSideSizeMobile = showLeftSideMobile ? leftSideSize : 0;
  const componentTheme = useComponentTheme(theme => theme.components.Footer, mode);
  const theme = useTheme();

  if (!leftSide && !rightSide && !bottomSide && !topSide) {
    return null;
  }

  const { footerHeight, largeFooterHeight } = theme.constants;

  const leftContent = leftSide && (
    <ColWrapper
      hideOnMobile={!showLeftSideMobile && !leftSideSizeMobile}
      key="left"
      lg={leftSideSize}
      reverseOrder={reverseOrder}
      sm={leftSideSizeMobile}
      tag={LeftSideWrapper}
      xs={leftSideSizeMobile}
    >
      {leftSide}
    </ColWrapper>
  );

  const rightContent = rightSide && (
    <Col
      key="right"
      lg={rightSideSize}
      reverseOrder={reverseOrder}
      sm={rightSideSizeMobile}
      tag={RightSideWrapper}
      xs={rightSideSizeMobile}
    >
      {rightSide}
    </Col>
  );

  const sideContent = [leftContent, rightContent];

  return (
    <Container className={className} data-test={dataTest} isFixed={isFixed} tag="footer">
      {mode === 'PageFooter' && !ignoreThemeHeight && (
        <Global
          styles={css`
            :root {
              --page-footer-height: ${topSide && bottomSide ? largeFooterHeight : footerHeight};
            }
          `}
        />
      )}

      {!!accessibilityTitle && (
        <h2 className="hide-text-show-focus" data-test="footer-accessibility-title">
          {accessibilityTitle}
        </h2>
      )}

      {(leftSide || rightSide) && (
        <SideWrapper
          componentTheme={componentTheme}
          data-test="footer-side-wrapper"
          footerHeight={footerHeight}
          ignoreThemeHeight={ignoreThemeHeight}
          reverseOrder={reverseOrder}
        >
          {reverseOrder ? sideContent.slice().reverse() : sideContent}
        </SideWrapper>
      )}

      {topSide ? (
        <SideWrapper
          block
          componentTheme={componentTheme}
          data-test="footer-side-wrapper"
          footerHeight={topSide && bottomSide ? largeFooterHeight : ''}
          ignoreThemeHeight={ignoreThemeHeight}
        >
          {topSide && topSide}
          {bottomSide && bottomSide}
        </SideWrapper>
      ) : (
        bottomSide && (
          <Col
            componentTheme={componentTheme}
            footerHeight={footerHeight}
            tag={fullWidth ? SideWrapper : BottomSideWrapper}
          >
            {bottomSide}
          </Col>
        )
      )}
    </Container>
  );
};

const Container = styled(Row)(
  ({ isFixed, theme }) => `
  z-index: ${theme.positioning.footer};
  box-shadow: ${theme.boxShadow.center};

  ${
    isFixed
      ? `
        position: fixed;
        bottom: 0;
        left: 0;
      `
      : 'position: relative;'
  };
`
);

const SideWrapper = styled.div(
  ({ block, componentTheme: { backgroundColor }, footerHeight, ignoreThemeHeight, reverseOrder, theme }) => `
    display: flex;
    align-items: center;
    ${reverseOrder ? 'flex-direction: row-reverse;' : ''}
    width: 100%;
    height: ${!ignoreThemeHeight ? footerHeight : ''};
    padding: 8px 4px;
    transition: height 0.25s ease;
    background: ${backgroundColor || transparentize(0.1, theme.color.white)};
    ${block && 'justify-content: space-between; flex-direction: column;'};

    @media (${theme.breakpoint.desktop}) {
      padding-left: 16px;
      padding-right: 16px;
    }

    @media (${theme.breakpoint.kiosk}) {
      padding: 12px 16px;
    }
  `
);

const LeftSideWrapper = styled.div(
  ({ reverseOrder }) => `
  display: flex;
  align-items: center;
  ${reverseOrder ? 'flex-direction: row-reverse; justify-content: flex-end;' : ''}
  height: 100%;
  padding-left: 3px;
`
);

const RightSideWrapper = styled.div(
  ({ reverseOrder, theme }) => `
  display: flex;
  ${reverseOrder ? 'flex-direction: row-reverse; justify-content: flex-end;' : ''}
  height: 100%;
  padding: 3px;

  @media (${theme.breakpoint.desktop}) {
    margin-left: auto;
  }
`
);

const BottomSideWrapper = styled.div`
  height: 100%;
  width: 100%;
  background: ${({ theme }) => transparentize(0.1, theme.color.white)};
`;

const ColWrapper = styled(Col)`
  ${({ hideOnMobile }) => hideOnMobile && `display: none;`};

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    display: inherit;
  }
`;
